import { useState, useEffect, useRef } from 'react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { useTranslation } from 'react-i18next';
import { Artist, CcConnectionStats } from 'types';

interface SelectedArtistsFloatProps {
  selectedArtists: Map<string, Artist>;
  onConnectSelected: () => void;
  isVisible?: boolean;
  connectionStats?: CcConnectionStats | null;
}

interface SelectedArtistsControlsProps {
  selectedArtists: Map<string, Artist>;
  onConnectSelected: () => void;
  connectionStats?: CcConnectionStats | null;
  remainingConnections: number;
  monthlyLimit: number;
  isDisabled: boolean;
  className?: string;
}

function SelectedArtistsControls({
  selectedArtists,
  onConnectSelected,
  connectionStats,
  remainingConnections,
  monthlyLimit,
  isDisabled,
  className = '',
}: SelectedArtistsControlsProps) {
  const { t } = useTranslation('collab-club');

  return (
    <div className={`flex items-start gap-2 flex-col lg:flex-row lg:items-center ${className}`}>
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="text-sm px-3 py-1 whitespace-nowrap">
          {selectedArtists.size === 0
            ? t('select_up_to', { limit: remainingConnections })
            : t('selected_count_with_limit', {
                count: selectedArtists.size,
                limit: remainingConnections,
              })}
        </Badge>
        <Button
          size="sm"
          disabled={isDisabled}
          className="bg-blue-500 hover:bg-blue-600"
          onClick={onConnectSelected}
        >
          {t('connect_selected')}
        </Button>
      </div>
      {connectionStats ? (
        <div className="text-gray-600 text-sm">
          {t('connection_status', {
            used: connectionStats.monthlyConnectionsUsed,
            limit: monthlyLimit,
            date: new Date(connectionStats.nextResetDate).toLocaleDateString('en-US', {
              dateStyle: 'medium',
            }),
          })}
        </div>
      ) : (
        <div className="text-red-600 text-sm">{t('no_profiles')}</div>
      )}
    </div>
  );
}

export function SelectedArtistsFloat({
  selectedArtists,
  onConnectSelected,
  isVisible = true,
  connectionStats,
}: SelectedArtistsFloatProps) {
  const [isFloating, setIsFloating] = useState(false);
  const originalRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const remainingConnections = connectionStats?.remainingConnections ?? 0;
  const monthlyLimit = connectionStats?.monthlyConnectionLimit ?? 0;
  const isDisabled =
    selectedArtists.size === 0 ||
    (connectionStats ? selectedArtists.size > remainingConnections : true) ||
    (connectionStats ? remainingConnections === 0 : true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the sentinel (original position) is not visible, float the component
        setIsFloating(!entry.isIntersecting);
      },
      {
        rootMargin: '-80px 0px 0px 0px', // Start floating when 80px above viewport
        threshold: 0,
      }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, []);

  if (!isVisible) {
    return <div ref={sentinelRef} className="h-0" />;
  }

  return (
    <>
      {/* Sentinel element to detect when original position scrolls out of view */}
      <div ref={sentinelRef} className="h-0" />

      {/* Original position (static) */}
      {!isFloating && (
        <div ref={originalRef} className="mt-4">
          <SelectedArtistsControls
            selectedArtists={selectedArtists}
            onConnectSelected={onConnectSelected}
            connectionStats={connectionStats}
            remainingConnections={remainingConnections}
            monthlyLimit={monthlyLimit}
            isDisabled={isDisabled}
          />
        </div>
      )}

      {/* Floating version */}
      {isFloating && (
        <div className="fixed top-20 md:top-4 left-1/2 transform -translate-x-1/2 z-50 bg-white shadow-lg rounded-lg border border-gray-200 px-4 py-3">
          <SelectedArtistsControls
            selectedArtists={selectedArtists}
            onConnectSelected={onConnectSelected}
            connectionStats={connectionStats}
            remainingConnections={remainingConnections}
            monthlyLimit={monthlyLimit}
            isDisabled={isDisabled}
            className="flex-col lg:flex-col items-center text-center"
          />
        </div>
      )}
    </>
  );
}
